import React, { useMemo } from "react";
import { StudentWithAttendance } from "@/hooks/useReportsData";
import { AttendanceReportFilters } from "../filters/AttendanceReportFilters";
import { ReportsFilters } from "../filters/ReportsFilters";
import { UnifiedStatsCard } from "../stats/UnifiedStatsCard";
import { TopStudentsSection } from "../sections/TopStudentsSection";
import { ReportsCharts } from "../charts/ReportsCharts";
import { StudentsTable } from "../tables/StudentsTable";
import { ColorLegend } from "../legends/ColorLegend";
import { AttendanceChartsSection } from "../charts/AttendanceChartsSection";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import { generateAttendanceReportPDF } from "@/utils/attendanceReportPdfGenerator";
import { formatHijriDateForPDF } from "@/utils/pdf/pdfUtils";

interface AttendanceReportTabProps {
  hasData: boolean;
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  selectedStage: string;
  handleStageChange: (value: string) => void;
  selectedGrade: string;
  handleGradeChange: (value: string) => void;
  selectedSection: string;
  setSelectedSection: (value: string) => void;
  sortType: "most_absent" | "best_attendance";
  setSortType: (value: "most_absent" | "best_attendance") => void;
  hasMultipleStages: boolean;
  validEnabledStages: any[];
  safeGetStageDisplayName: (stage: any) => string;
  filteredAvailableGrades: string[];
  availableSectionsForGrade: string[];
  filteredStudents: StudentWithAttendance[];
  studentsWithAlerts: StudentWithAttendance[];
  studentsWithLateAlerts: StudentWithAttendance[];
  mostAbsentStudents: StudentWithAttendance[];
  bestAttendanceStudents: StudentWithAttendance[];
  weeklyData: any[];
  gradeData: any[];
  overallStats: any;
  getSelectedStageDisplayName: () => string;
  periodDateRange?: { startDate: Date; endDate: Date };
}
export const AttendanceReportTab = React.memo(function AttendanceReportTab({
  hasData,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedStage,
  handleStageChange,
  selectedGrade,
  handleGradeChange,
  selectedSection,
  setSelectedSection,
  sortType,
  hasMultipleStages,
  validEnabledStages,
  safeGetStageDisplayName,
  filteredAvailableGrades,
  availableSectionsForGrade,
  filteredStudents,
  studentsWithAlerts,
  studentsWithLateAlerts,
  mostAbsentStudents,
  bestAttendanceStudents,
  weeklyData,
  gradeData,
  overallStats,
  getSelectedStageDisplayName,
  periodDateRange,
}: AttendanceReportTabProps) {
  const { settings } = useRealSchoolSettings();

  // Sort students based on selected sort type
  const sortedStudents = useMemo(() => {
    return [...filteredStudents].sort((a, b) => {
      if (sortType === "best_attendance") {
        // Sort by attendance percentage (highest first)
        return (b?.attendancePercentage || 0) - (a?.attendancePercentage || 0);
      } else {
        // Default: Sort by absence days (highest first)
        return (b?.absenceDays || 0) - (a?.absenceDays || 0);
      }
    });
  }, [filteredStudents, sortType]);

  // Calculate statistics based on filtered students and period range
  const attendanceStats = useMemo(() => {
    if (!periodDateRange) {
      return {
        totalRecords: 0,
        presentCount: 0,
        absenceCount: 0,
        presentPercentage: 0,
        absencePercentage: 0,
        totalDays: 0,
      };
    }

    // Calculate total possible records (students × days)
    const startDate = periodDateRange.startDate;
    const endDate = periodDateRange.endDate;
    const daysDiff =
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

    const totalPossibleRecords = filteredStudents.length * daysDiff;

    // Calculate actual present and absent days from filtered students
    const totalPresentDays = filteredStudents.reduce((sum, student) => {
      return (
        sum + Math.round(((student.attendancePercentage || 0) / 100) * daysDiff)
      );
    }, 0);

    const totalAbsentDays = totalPossibleRecords - totalPresentDays;
    // console.log('totalPossibleRecords: ', totalPossibleRecords);
    // console.log('totalPresentDays: ', totalPresentDays);
    // console.log('totalAbsentDays: ', totalAbsentDays);
    const presentPercentage =
      totalPossibleRecords > 0
        ? Math.round((totalPresentDays / totalPossibleRecords) * 100)
        : 0;
    const absencePercentage =
      totalPossibleRecords > 0
        ? Math.round((totalAbsentDays / totalPossibleRecords) * 100)
        : 0;

    const studentsTotalAbsentDays = filteredStudents.reduce((sum, student) => {
      return sum + student.absenceDays;
    }, 0);
    return {
      totalRecords: totalPossibleRecords,
      presentCount: totalPresentDays,
      absenceCount: totalAbsentDays,
      presentPercentage,
      absencePercentage,
      totalDays: daysDiff,
      studentsCounts: filteredStudents.length,
      studentsTotalAbsentDays,
    };
  }, [filteredStudents, periodDateRange]);

  // Calculate late recurrence rate
  const lateRecurrenceRate = useMemo(() => {
    const studentsWithLateDays = filteredStudents.filter(
      (student) => (student.lateDays || 0) > 0
    );
    if (filteredStudents.length === 0) return 0;
    return Math.round(
      (studentsWithLateDays.length / filteredStudents.length) * 100
    );
  }, [filteredStudents]);

  // Get period display text with Hijri dates and Arabic numerals
  const getPeriodDisplayText = () => {
    if (startDate && endDate) {
      return `من ${formatHijriDateForPDF(
        startDate
      )} إلى ${formatHijriDateForPDF(endDate)}`;
    } else if (startDate) {
      return `من ${formatHijriDateForPDF(startDate)}`;
    }
    return "جميع الفترات";
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    await generateAttendanceReportPDF(
      sortedStudents,
      overallStats,
      {
        selectedGrade: selectedGrade || "جميع الصفوف",
        selectedSection,
        selectedStage,
        periodDateRange,
        weeklyData,
      },
      attendanceStats
    );
  };

  return (
    <div dir="rtl">
      {/* زر التنزيل مرئي دائماً لكن غير نشط حتى تحديد جميع الشروط */}
      <ReportsFilters
        studentsWithLateAlertsCount={studentsWithLateAlerts.length}
        overallLateRate={overallStats.overallLateRate || 0}
        filteredStudents={sortedStudents}
        overallStats={overallStats}
        selectedGrade={selectedGrade}
        selectedSection={selectedSection}
        selectedStage={selectedStage}
        periodDateRange={periodDateRange}
        weeklyData={weeklyData}
        hasMultipleStages={hasMultipleStages}
        attendanceStats={attendanceStats}
      />

      {/* Attendance Report Filters */}
      <AttendanceReportFilters
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedStage={selectedStage}
        handleStageChange={handleStageChange}
        selectedGrade={selectedGrade}
        handleGradeChange={handleGradeChange}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        validEnabledStages={validEnabledStages}
        safeGetStageDisplayName={safeGetStageDisplayName}
        filteredAvailableGrades={filteredAvailableGrades}
        availableSectionsForGrade={availableSectionsForGrade}
      />

      {/* عرض البيانات فقط عند تحديد تاريخ البداية */}
      {startDate ? (
        <>
          <UnifiedStatsCard
            totalStudents={filteredStudents.length}
            attendanceRate={attendanceStats.presentPercentage}
            alertsCount={studentsWithAlerts.length}
            absencePercentage={attendanceStats.absencePercentage}
          />
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 text-lg">
            يرجى تحديد تاريخ البداية لعرض التقرير
          </p>
        </div>
      )}

      {startDate && hasData && (
        <>
          <StudentsTable
            students={sortedStudents}
            sortBy={
              sortType === "best_attendance"
                ? "attendancePercentage"
                : "absenceDays"
            }
            sortOrder="desc"
            onSort={() => {}}
          />

          <div className="mb-6">
            <ColorLegend alertSettings={settings?.alert_rules} />
          </div>

          <TopStudentsSection
            mostAbsentStudents={mostAbsentStudents}
            bestAttendanceStudents={bestAttendanceStudents}
            selectedPeriod={getPeriodDisplayText()}
          />



          <AttendanceChartsSection filteredStudents={filteredStudents} />
        </>
      )}
    </div>
  );
});
